/**
 * Dialog-Shell für die Vario-Modale der Kaufland-App (/#/install, /#/uninstall).
 *
 * Zwei Aufgaben:
 *
 * 1. **Auto-Grow-Marker.** Install/Uninstall laufen NICHT als Vollflächen-Slot, sondern in
 *    Varios Installations-Modal mit Varios eigenem Button unter dem iframe. Deshalb kein
 *    `useFullHeight` (das schiebt den Button weit nach unten) und kein fixer 900er-Korridor
 *    (Leerraum um ein Zwei-Feld-Formular), sondern `initVarioIframeHeight('auto')` in main.ts:
 *    das iframe wächst auf die Inhaltshöhe. Damit die gemeldete `body.scrollHeight` überhaupt
 *    den Inhalt spiegelt, muss die `bridge.css`-§16-Kette (`html/body/main#app { height:100%;
 *    overflow:hidden }`) hier aufgebrochen werden — sonst meldet die App immer nur die
 *    Viewport-Höhe und die Karte wird unten abgeschnitten. Spiegel von `html.otto-autogrow`.
 *
 * 2. **Dialog-Variante des kf-page-Fensters.** `.kf-page` ist auf ein bildschirmfüllendes
 *    Fenster ausgelegt (`height:100%`, `overflow:hidden`, `.card{flex:1}`). Für einen Dialog
 *    mit Inhaltshöhe lösen wir das über `.kf-page.kf-dialog` auf und ergänzen die Karten-
 *    Bausteine (Intro, Sektions-Label, Formular, Fuß-Leiste, Ergebnis-Karte). Alles bleibt
 *    unter `.kf-page` gescoped — kein globaler Reset, keine Änderung am geteilten Package.
 */

const STYLE_ID = 'kf-dialog-styles';

/**
 * Dialog-Icons, die `KF_ICON` (packages/frontend-design) nicht abdeckt: Melde-Zeilen-Symbole
 * und das große Häkchen der Ergebnis-Karte. Gleiche Machart wie KF_ICON — 24×24-Stroke-SVG
 * auf `currentColor`, damit sie die Farbe des umgebenden Zustands übernehmen.
 */
export const KF_DLG_ICON = {
  info: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 7.5h.01"/></svg>',
  alert: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 8v4.5M12 16.5h.01"/></svg>',
  check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6 9 17l-5-5"/></svg>',
  checkBold: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>',
  trash: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/><path d="M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12"/></svg>',
} as const;

/**
 * Injiziert die Dialog-Styles (einmalig) und setzt den Auto-Grow-Marker auf `<html>`.
 * Von `renderInstall`/`renderUninstall` als erstes aufrufen.
 */
export function ensureKfDialogShell(): void {
  document.documentElement.classList.add('kf-dialog-autogrow');
  if (document.getElementById(STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    /* --- 1. Auto-Grow: bricht die bridge.css-§16-100%-Kette auf (höhere Specificity) --- */
    html.kf-dialog-autogrow,
    html.kf-dialog-autogrow body,
    html.kf-dialog-autogrow main#app { height:auto; overflow:visible; }
    html.kf-dialog-autogrow main#app > section#content { flex:0 0 auto; }

    /* --- 2. kf-page als Dialog: Höhe = Inhalt statt Viewport --- */
    .kf-page.kf-dialog { height:auto; min-height:0; overflow:visible; display:block; }
    .kf-page.kf-dialog .main { display:block; padding:16px 20px 20px; overflow:visible; }
    .kf-page.kf-dialog .apphead { margin-bottom:14px; }
    .kf-page.kf-dialog .card {
      flex:0 0 auto; display:block; overflow:visible;
      max-width:660px; padding:22px 24px;
    }

    /* Status-Pill im Header: neutral, solange nichts verbunden ist (Default ist grün). */
    .kf-page.kf-dialog .hstatus.pending .d { background:var(--muted); box-shadow:0 0 0 3px rgba(115,115,115,.14); }
    .kf-page.kf-dialog .hstatus.error .d { background:var(--danger); box-shadow:0 0 0 3px rgba(229,72,77,.16); }

    /* --- Karten-Innenleben --- */
    .kf-page.kf-dialog .dlg-intro { font-size:13.5px; line-height:1.55; color:var(--ink-soft); margin:0 0 18px; }
    .kf-page.kf-dialog .dlg-intro b { font-weight:600; color:var(--ink); }
    .kf-page.kf-dialog .sec-lab {
      font-size:10.5px; font-weight:600; letter-spacing:.11em; text-transform:uppercase;
      color:var(--muted); padding-bottom:9px; margin-bottom:14px; border-bottom:1px solid #F0F0F1;
    }
    .kf-page.kf-dialog .dlg-fields { display:flex; flex-direction:column; gap:13px; }
    .kf-page.kf-dialog .dlg-field label {
      display:block; font-size:11px; font-weight:600; letter-spacing:.06em;
      text-transform:uppercase; color:var(--muted); margin-bottom:6px;
    }
    .kf-page.kf-dialog .dlg-field input {
      font-family:inherit; font-size:13.5px; color:var(--ink); width:100%;
      background:#fff; border:1px solid #E8E8EB; border-radius:9px;
      padding:0 13px; height:40px; outline:none; transition:all .15s;
    }
    .kf-page.kf-dialog .dlg-field input:focus {
      border-color:var(--vario-green); box-shadow:0 0 0 3px rgba(132,185,51,.14);
    }
    .kf-page.kf-dialog .dlg-hint { font-size:12px; line-height:1.5; color:var(--muted); margin:14px 0 0; }
    .kf-page.kf-dialog .dlg-hint a { color:var(--vario-green-dark); font-weight:500; }

    /* Melde-Zeile (Prüfen/Erfolg/Fehler) — ersetzt .install-status im Dialog-Design. */
    .kf-page.kf-dialog .dlg-msg {
      display:none; align-items:flex-start; gap:8px; margin-top:16px;
      font-size:12.5px; line-height:1.5; border-radius:9px; padding:10px 12px;
    }
    .kf-page.kf-dialog .dlg-msg.show { display:flex; }
    .kf-page.kf-dialog .dlg-msg svg { width:15px; height:15px; flex:none; margin-top:1px; }
    .kf-page.kf-dialog .dlg-msg.info { background:#F4F4F5; color:var(--ink-soft); }
    .kf-page.kf-dialog .dlg-msg.ok { background:var(--vario-green-tint); color:#4A7A17; }
    .kf-page.kf-dialog .dlg-msg.error { background:#FEF3F2; color:#B42318; }

    /* Fortschritts-Schritte (Deinstallation). */
    .kf-page.kf-dialog .dlg-steps { display:flex; flex-direction:column; gap:11px; }
    .kf-page.kf-dialog .dlg-step { display:flex; align-items:center; gap:11px; font-size:13px; color:var(--muted); }
    .kf-page.kf-dialog .dlg-step-ic {
      width:22px; height:22px; flex:none; border-radius:50%;
      display:inline-flex; align-items:center; justify-content:center;
      background:var(--field); color:var(--muted);
    }
    .kf-page.kf-dialog .dlg-step-ic svg { width:13px; height:13px; }
    .kf-page.kf-dialog .dlg-step.running { color:var(--ink-soft); }
    .kf-page.kf-dialog .dlg-step.running .dlg-step-ic { background:var(--vario-green-tint); }
    .kf-page.kf-dialog .dlg-step.running .dlg-spin { border-color:rgba(132,185,51,.3); border-top-color:var(--vario-green-dark); }
    .kf-page.kf-dialog .dlg-step.done { color:var(--ink); }
    .kf-page.kf-dialog .dlg-step.done .dlg-step-ic { background:var(--vario-green); color:#fff; }
    .kf-page.kf-dialog .dlg-step.failed .dlg-step-ic { background:#FEE4E2; color:#B42318; }

    /* Fuß-Leiste der Karte (randlos bis zum Kartenrand). */
    .kf-page.kf-dialog .dlg-foot {
      display:flex; align-items:center; gap:10px; justify-content:flex-end;
      margin:20px -24px -22px; padding:15px 24px; border-top:1px solid #F0F0F1;
    }
    .kf-page.kf-dialog .btn[disabled] { opacity:.55; cursor:not-allowed; box-shadow:none; }

    /* Inline-Spinner für den laufenden Button (kf-page arbeitet mit Inline-SVG, nicht FA). */
    .kf-page.kf-dialog .dlg-spin {
      width:14px; height:14px; flex:none; border-radius:50%;
      border:2px solid rgba(255,255,255,.45); border-top-color:#fff;
      animation:kf-dlg-spin .7s linear infinite;
    }
    .kf-page.kf-dialog .btn-ghost .dlg-spin { border-color:rgba(0,0,0,.18); border-top-color:var(--ink-soft); }
    @keyframes kf-dlg-spin { to { transform:rotate(360deg); } }

    /* Ergebnis-Karte (Installation fertig / abgebrochen / deinstalliert). */
    .kf-page.kf-dialog .dlg-result { text-align:center; padding:10px 4px 14px; }
    .kf-page.kf-dialog .dlg-result-ic {
      width:52px; height:52px; margin:0 auto 16px; border-radius:50%;
      display:flex; align-items:center; justify-content:center;
      background:var(--vario-green-tint); color:var(--vario-green-dark);
    }
    .kf-page.kf-dialog .dlg-result-ic.neutral { background:#F4F4F5; color:var(--muted); }
    .kf-page.kf-dialog .dlg-result-ic.error { background:#FEF3F2; color:#B42318; }
    .kf-page.kf-dialog .dlg-result-ic svg { width:26px; height:26px; }
    .kf-page.kf-dialog .dlg-result h2 { font-size:17px; font-weight:600; margin:0 0 8px; color:var(--ink); }
    .kf-page.kf-dialog .dlg-result p { font-size:13px; line-height:1.55; color:var(--muted); margin:0 auto; max-width:430px; }
    .kf-page.kf-dialog .dlg-result .mono {
      display:inline-block; margin-top:16px; font-size:11.5px; color:var(--muted);
      background:var(--field); border-radius:7px; padding:6px 11px;
    }
  `;
  document.head.appendChild(style);
}

/** Statisches HTML escapen (Fehlermeldungen von der API landen im innerHTML der Melde-Zeile). */
export function kfEscapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

export interface KfDialogResult {
  kind: 'ok' | 'neutral' | 'error';
  /** Inline-SVG (KF_ICON / KF_DLG_ICON). */
  icon: string;
  title: string;
  text: string;
  /** Optionale Monospace-Fußnote unter dem Text. */
  note?: string | undefined;
}

/**
 * Ersetzt den Karten-Inhalt durch eine Ergebnis-Karte (installiert / abgebrochen / deinstalliert).
 * Das Formular ist danach bewusst weg — Installation und Deinstallation sind Einweg-Vorgänge;
 * Nachkorrekturen an den Zugangsdaten laufen über den Zugangsdaten-Tab (`/#/credentials`).
 */
export function showKfDialogResult(card: HTMLElement, o: KfDialogResult): void {
  const icClass = o.kind === 'ok' ? 'dlg-result-ic' : `dlg-result-ic ${o.kind}`;
  card.innerHTML = `
    <div class="dlg-result">
      <div class="${icClass}">${o.icon}</div>
      <h2>${kfEscapeHtml(o.title)}</h2>
      <p>${kfEscapeHtml(o.text)}</p>
      ${o.note ? `<div class="mono">${kfEscapeHtml(o.note)}</div>` : ''}
    </div>`;
}
